/**
 * Cross-lab strain families (PLAN-YEAST-FAMILIES).
 *
 * Each record is one strain and every catalog entry that is a cut of it. The
 * schema's own docblock argues why a family is a record rather than a slug on
 * the yeast; what follows is the rule membership is authored by, because it is
 * the only thing standing between this file and a forum thread.
 *
 * ## What earns a place
 *
 * A member is a lab's **cut of the same organism**, banked and sold under that
 * lab's own code. In practice one of:
 *
 * - the catalog's own record already says so (`white-labs-wlp001` — "the Chico
 *   strain in a White Labs vial"; `cellarscience-german` — "Weihenstephan
 *   34/70 in a CellarScience sachet"), or
 * - the product is one of the two or three that *define* the strain for
 *   brewers and appear as it in every reference — `fermentis-us-05`,
 *   `wyeast-1056`, `fermentis-w-34-70`, or
 * - a published cross-reference puts it there and nothing here contradicts it.
 *   The BrewGinner comparison chart is the one leaned on so far; it is the
 *   reason `imperial-a07`, `imperial-l17`, `omega-oyl-114`, `white-labs-wlp860`
 *   and `wyeast-2352` are in families that our own records do not name.
 *
 * ## A chart is a lead, not a verdict
 *
 * The same chart carries **two contradictory Urquell rows** — one pairing
 * `wyeast-2001` and `omega-oyl-101` with `white-labs-wlp800`, and a second,
 * separately headed "Urquell", pairing `white-labs-wlp802` and `wyeast-2278`
 * with Imperial's Urkel. The second row is Budweiser Budvar in České
 * Budějovice, a different brewery in a different city from Plzeňský Prazdroj.
 * A chart that files one brewery under two headings and another brewery under
 * one of them is a starting point for reading, not a source to copy.
 *
 * So a row is followed where it agrees with what is written here, and
 * questioned where it does not. Both Urquell rows are declined below, and five
 * cells were dropped out of rows that otherwise shipped whole:
 *
 * - `fermentis-t-58` (Celis White) and `fermentis-wb-06` (Weihenstephan 66) —
 *   **the chart drops them itself**, annotating both rows "Fermentis may not be
 *   an exact match". A source that flags its own weak cells has earned the
 *   right to be believed about the rest.
 * - `fermentis-k-97` (Düsseldorf) — our record calls it "kölsch-type", not alt.
 *   It is genuinely used for both, which is how it ended up on the row, but a
 *   family is a claim about the organism rather than about the styles it suits.
 * - `wyeast-2487` (Ayinger) — our record says "an Austrian alpine strain", and
 *   Aying is in Bavaria. One of the two is wrong and it is not resolvable here.
 * - `imperial-a20` (Unicorn Dust) — described here as "the New England strain
 *   that made hazy IPA possible", which is what this catalog also says about
 *   `wyeast-1318`. Two entries cannot both be that strain, and the family it
 *   was offered for is the reclassified Brett Trois, which is a third thing.
 *
 * ## Three things that look like membership and are not
 *
 * All three were live candidates while this file was written, and each one is
 * a different way of being nearly right:
 *
 * - **Engineered derivatives.** `berkeley-fresh-chico` is Chico modified to
 *   express ALDC; `bsi-x-tropica` is A-56 modified to release thiols;
 *   `omega-oyl-437` is "the engineered answer to 34/70". Each ferments like its
 *   parent and none of them *is* its parent — which is the whole reason a
 *   brewery buys one.
 * - **A lab's own published substitute.** `aeb-fermolager-w` is what AEB's
 *   substitution guide lines up against W-34/70. That is a recommendation to
 *   swap, not a claim of identity, and this catalog does not model swaps yet.
 * - **A passing comparison.** `escarpment-premium-pils` is described as slower
 *   than "a German 34/70 type"; `fermentis-s-189` shares a sentence with it and
 *   is Hürlimann. A strain named in another strain's description is usually
 *   being contrasted with it.
 *
 * Two products from one lab in the same family is fine and not a mistake:
 * `bsi-3470`, `bsi-830` and `bsi-l-24` are three separately-sourced cuts BSI
 * lists separately, and a brewer choosing between them wants to know that.
 *
 * ## One brewery is not one strain
 *
 * The trap that cost the most care here. A famous beer is often fermented by
 * more than one organism, and the labs bank them separately — so a family named
 * after the *beer* quietly asserts an identity the brewery itself does not
 * claim.
 *
 * - **Pilsner Urquell** ships as the H-strain and as other cuts. Only
 *   `wyeast-2001` and `omega-oyl-101` name the H-strain, so that is the family,
 *   and it is named for the strain rather than for the brewery.
 *   `white-labs-wlp800` is widely called Urquell — the chart says so too — and
 *   says only "a Czech pilsner strain" here. Being the brewery's yeast and
 *   being the H-strain are different claims, so it stays out until something
 *   makes the second one. `wyeast-2278` and Imperial's Urkel stay out of
 *   Budějovice for the same reason: the only source offering them is the
 *   mislabelled row.
 * - **Whitbread** is at least three things. `wyeast-1099` and `bsi-a-99` are
 *   the soft, malty one. `wyeast-1098` is the dry, crisp one our own record
 *   contrasts it against, and `white-labs-wlp017` is "Whitbread II", a mixed
 *   culture. One family, the B strain, and the other two left alone.
 * - **Kölsch** is two families, not one. `wyeast-2565` is the Weihenstephan 165
 *   strain and `white-labs-wlp029` is from a Cologne brewpub, they behave
 *   differently in their own descriptions, and the cross-reference chart lists
 *   them on separate rows under separate headings. Reading that agreement the
 *   other way round is what finally split them.
 * - **Voss kveik** is the awkward one, and it ships with its own caveat in its
 *   description. Fourteen entries carry the tag; eleven trace to Sigmund
 *   Gjernes's culture and are grouped. But kveik is a *culture* — several
 *   organisms living together — so "the same strain" is looser here than
 *   anywhere else in this file, and two of our own records say so out loud:
 *   `kveik-yeastery-k1` sells it "as a bacteria-free multi-strain, not a single
 *   isolate", and `yeast-bay-sigmunds-voss` sells it "as Yeast Bay isolated
 *   it". Those are different products from one origin, which is still worth
 *   telling a brewer about.
 *
 * ## Candidates deliberately not shipped
 *
 * Two Voss packs are declined on the word "type", which their makers chose and
 * we should not overrule. `inland-island-444` is "a Voss-*type* marmalade
 * character **with cantaloupe, citronella and white pepper alongside**" — the
 * extra notes are a different organism, not a drifted cut. `mangrove-jacks-m12`
 * is "a Voss-*type* kveik" whose maker publishes no origin at all; the profile
 * matches, and matching is not provenance.
 *
 * `lallemand-bry-97` is widely listed as Chico.
 * Lallemand does not say so and neither does this catalog's record, so it waits
 * for a source rather than riding on the strength of the rest. The same
 * applies to `cellarscience-big-ben` and `lallemand-london` against London ESB
 * — the second is maltotriose-negative, which is a different organism, not a
 * drifted cut.
 */

import type { YeastFamily } from '../../schema/yeast-family';

export const yeastFamilies: readonly YeastFamily[] = [
  {
    id: 'chico',
    name: 'Chico',
    description:
      "The American ale strain, out of Sierra Nevada's brewery in Chico and into almost every lab's catalog under a different code. It is the yeast most other clean American strains are described against, and the reason a West Coast pale ale tastes of hops rather than of fermentation. Engineered derivatives of it — thiol-releasing and diacetyl-suppressing cuts — are sold too, and are not the same organism.",
    members: [
      'fermentis-us-05',
      'wyeast-1056',
      'white-labs-wlp001',
      'white-labs-wlp001-dry',
      'omega-oyl-004',
      'bsi-a-56',
      'bsi-1',
      'rva-101',
      'jasper-jy215',
      'cellarscience-cali',
      'aeb-fermoale-ay4',
      'bootleg-classic-american',
      'imperial-a07',
    ],
  },
  {
    id: 'weihenstephan-34-70',
    name: 'Weihenstephan 34/70',
    description:
      'The most widely used lager strain in the world, released by the Weihenstephan collection and sold by everyone. Clean and crisp with enough malt left behind to carry a pilsner, and forgiving enough at the warm end of lager temperatures that it has become the default answer for almost any bottom-fermented beer. Unusually for a family here, it has been sequenced outside the labs that sell it.',
    members: [
      'fermentis-w-34-70',
      'wyeast-2124',
      'white-labs-wlp830',
      'bsi-3470',
      'bsi-830',
      'bsi-l-24',
      'cellarscience-german',
      'imperial-l13',
      'omega-oyl-106',
    ],
  },
  {
    id: 'augustiner',
    name: 'Augustiner',
    description:
      'The Munich house lager strain, out of Augustiner-Bräu and into the two catalogs that list it by name. Richly malty and low in both diacetyl and sulfur, and willing to work a little warmer than most lager yeasts — helles first, but it carries a pilsner or a dunkel without complaint.',
    members: [
      'bsi-augustiner',
      'jasper-jy218',
      'imperial-l17',
      'omega-oyl-114',
      'white-labs-wlp860',
      'wyeast-2352',
    ],
  },
  {
    id: 'pilsner-urquell-h',
    name: 'Pilsner Urquell H-strain',
    description:
      'The H-strain of the Pilsen brewery, which ferments with more than one organism — so this family is named for the strain and not for the beer. Dry and neutral with a light floral note over a fuller mouthfeel than the finish suggests. Other Czech pilsner strains sold as "Urquell" are not assumed to be this one.',
    members: ['wyeast-2001', 'omega-oyl-101'],
  },
  {
    id: 'budejovice',
    name: 'Budějovice (Budvar)',
    description:
      'The southern Bohemian lager strain from Budweiser Budvar, in České Budějovice: a malty nose over a crisp, dry, well-balanced finish that leaves room for late hop character. It rewards a slow cold fermentation and a long lagering, and it is the strain American light lager is usually traced back to.',
    members: ['wyeast-2000', 'white-labs-wlp802', 'omega-oyl-100'],
  },
  {
    id: 'whitbread-b',
    name: 'Whitbread B',
    description:
      'The soft, malty Whitbread cut — mildly fruity, stopping well short of dry, and flocculent enough to clear without filtration. Not to be confused with the dry, crisp Whitbread strain each of these labs also sells, which both of their catalogs describe by contrast with this one.',
    members: ['wyeast-1099', 'bsi-a-99'],
  },
  {
    id: 'ringwood',
    name: 'Ringwood',
    description:
      'The Ringwood strain: malt-forward, distinctly estery and famously demanding. It wants far more oxygen than most ale yeasts, is slow at both ends of the ferment, and genuinely needs its diacetyl rest — the character is worth the handling, and every lab that sells it says so.',
    members: ['wyeast-1187', 'bsi-1187'],
  },
  {
    id: 'london-esb',
    name: 'London ESB',
    description:
      'The London ESB strain: malty, fruity, and so flocculent that it can quit early in a tall fermenter — rouse it if the gravity stalls. It under-attenuates by design, which is exactly what leaves an ESB its body.',
    members: ['wyeast-1968', 'white-labs-wlp002', 'omega-oyl-016', 'imperial-a09'],
  },
  {
    id: 'conan',
    name: 'Conan',
    description:
      'The Vermont strain behind the modern hazy IPA: peach, apricot and pineapple that amplify fruity hop character rather than competing with it, over a soft body and slow flocculation that keeps the haze where the style wants it. Several labs also sell engineered descendants of it, which are not in this family.',
    members: ['omega-oyl-052', 'jasper-jy127', 'rva-104', 'imperial-a04', 'white-labs-wlp095'],
  },
  {
    id: 'weihenstephan-weizen',
    name: 'Weihenstephan Weizen',
    description:
      'The weizen strain from the Weihenstephan collection, and the reference point for the banana-and-clove balance every other wheat yeast is described against. Pitch rate, gravity and temperature all move that balance, so the decision is made before the fermenter is set. It stays in suspension on purpose.',
    members: ['wyeast-3068', 'white-labs-wlp300', 'omega-oyl-021', 'imperial-g01'],
  },
  {
    id: 'whitbread-dry',
    name: 'Whitbread dry',
    description:
      'The dry, crisp, well-attenuating Whitbread cut, with a mild tartness and a clean finish that suits bitters, milds and English pale ales. The other Whitbread strain is softer and maltier and has its own family here; the two are routinely sold under near-identical names, which is exactly why they are kept apart.',
    members: [
      'wyeast-1098',
      'fermentis-s-04',
      'white-labs-wlp007',
      'imperial-a01',
      'omega-oyl-006',
    ],
  },
  {
    id: 'kolsch-weihenstephan-165',
    name: 'Kölsch (Weihenstephan 165)',
    description:
      'The kölsch strain from the Weihenstephan collection: crisp and faintly fruity, fermenting like an ale and conditioning like a lager. It flocculates poorly and is slow to clear, which is the honest way to tell it apart from the other kölsch strain the same labs sell.',
    members: ['wyeast-2565', 'omega-oyl-017'],
  },
  {
    id: 'pj-fruh',
    name: 'P. J. Früh',
    description:
      'The other kölsch strain — from a Cologne brewpub rather than the Weihenstephan collection — and the one that drops bright with a cold rest instead of hanging in suspension. Clean and faintly fruity, and hop-friendly enough that late dry hopping is a reasonable idea.',
    members: ['white-labs-wlp029', 'omega-oyl-044', 'imperial-g03'],
  },
  {
    id: 'voss-kveik',
    name: 'Voss kveik',
    description:
      'Sigmund Gjernes\'s farmhouse culture from Voss in western Norway, and the kveik nearly every brewer meets first: orange-peel citrus that gets louder as the temperature climbs, a hard drop, and a finish inside three days at 35 °C. It is a culture rather than a single organism, so the packs differ — some labs ship the whole community, others one isolate taken out of it. They brew alike, which is why they are grouped, but "the same strain" is looser here than anywhere else in this file.',
    members: [
      'escarpment-voss-kveik',
      'kveik-yeastery-k1',
      'white-labs-wlp520',
      'yeast-bay-sigmunds-voss',
      'bsi-voss-kveik',
      'omega-oyl-061',
      'cellarscience-voss',
      'aeb-fermo-kveik-sv',
      'whc-voss-kveik',
      'cml-no-1-voss',
      'imperial-a43',
    ],
  },
  {
    id: 'guinness',
    name: 'Guinness',
    description:
      'The Irish stout strain, out of one of the oldest stout breweries in the world: faintly tart, slightly diacetyl-forward and dry enough at the finish that roast and crystal malt speak for themselves rather than being layered over with fruit. A little minerality underneath keeps the roast from turning harsh.',
    members: ['wyeast-1084', 'white-labs-wlp004', 'omega-oyl-005', 'imperial-a10'],
  },
  {
    id: 'anchor-liberty',
    name: 'Anchor Liberty',
    description:
      'An American ale strain with more of its own character than Chico — slightly nutty, faintly tart, a touch more ester — and better flocculation with it. Leans English where Chico leans neutral, which is what makes it the pick when a pale ale should taste of something besides its hops.',
    members: ['wyeast-1272', 'white-labs-wlp051', 'imperial-a15'],
  },
  {
    id: 'rogue-pacman',
    name: 'Rogue (Pacman)',
    description:
      'The Rogue brewery strain: dry and clean with very little diacetyl and only a whisper of ester, dropping harder than Chico does. A Northwest pale ale yeast that ferments reliably at the cool end, where most American strains start to stall.',
    members: ['rva-103', 'imperial-a18'],
  },
  {
    id: 'edinburgh',
    name: 'Edinburgh (McEwan)',
    description:
      'The Scottish ale strain, at home in wee heavy, 80 shilling and anything else built on malt: pear and melon esters underneath, body left behind rather than attenuated away, and a temperature range wide enough to serve as a house yeast.',
    members: ['wyeast-1728', 'white-labs-wlp028', 'imperial-a31'],
  },
  {
    id: 'london-ale-iii',
    name: 'London Ale III (Boddingtons)',
    description:
      'The strain that made hazy IPA possible: soft, fruity and low-attenuating, leaving body and a full mouthfeel behind, and biotransforming a dry hop rather than shouting over it. It flocculates poorly on purpose — the haze is the point.',
    members: ['wyeast-1318', 'omega-oyl-011', 'imperial-a38'],
  },
  {
    id: 'westmalle',
    name: 'Westmalle',
    description:
      'The Trappist high-gravity strain from Westmalle: phenolic and estery, dark fruit against restrained clove, and able to take a big wort to a dry finish. It wants a warm finish and a great deal of healthy yeast, and it is the mainstay of dubbel, tripel and quadrupel.',
    members: ['wyeast-3787', 'white-labs-wlp530', 'omega-oyl-028', 'imperial-b48'],
  },
  {
    id: 'chimay',
    name: 'Chimay',
    description:
      'The Chimay monastery strain: aromatic, phenol-forward and spicy, with the alcohol tolerance for quads and tripels. It stays in suspension a long time and wants a warm finish, both of which are the price of the ester profile.',
    members: ['wyeast-1214', 'white-labs-wlp500', 'imperial-b63'],
  },
  {
    id: 'celis-white',
    name: 'Celis White',
    description:
      'The Belgian witbier strain, mildly phenolic and tart, giving the herbal aroma that carries coriander and orange peel rather than competing with them. Versatile enough for spiced beers generally, not only for wit.',
    members: ['wyeast-3944', 'white-labs-wlp400', 'imperial-b44'],
  },
  {
    id: 'dusseldorf-alt',
    name: 'Düsseldorf alt',
    description:
      'The Düsseldorf altbier strain: ferments fast and cold, finishes very dry, and contributes almost no ester of its own, which is what leaves a clean malty German brown tasting of its malt. The classic choice for alt and for German ambers.',
    members: ['wyeast-1007', 'white-labs-wlp036', 'imperial-g02'],
  },
  {
    id: 'california-common',
    name: 'California common (Anchor)',
    description:
      "The steam-beer strain: a lager yeast that ferments happily at ale temperatures and still finishes like a lager, with a woody, faintly minty note that is the style's signature. It flocculates well and needs none of the cold a lager usually demands.",
    members: ['wyeast-2112', 'white-labs-wlp810', 'imperial-l05'],
  },
  {
    id: 'ayinger',
    name: 'Ayinger',
    description:
      'A southern Bavarian lager strain, balanced between malt and hop rather than leaning either way, and at home across bock, doppelbock, Oktoberfest and helles. The traditional pick when a malty lager should stay drinkable rather than turning syrupy.',
    members: ['white-labs-wlp833', 'omega-oyl-111'],
  },
  {
    id: 'grupo-modelo',
    name: 'Grupo Modelo',
    description:
      'The Mexican lager strain: clean, crisp and bright across a wide fermentation range, and as comfortable in a dark lager, a bock or a märzen as in the pale adjunct lager it is named for.',
    members: ['white-labs-wlp940', 'omega-oyl-113'],
  },
  {
    id: 'brett-trois',
    name: 'Brett Trois (reclassified)',
    description:
      'Long sold as a Brettanomyces and since reclassified as a diastatic Saccharomyces, which is the rare case of a family whose members changed species on paper without changing in the fermenter. Slightly tart, mango and tropical fruit, and diastatic — it keeps attenuating in package, so treat it accordingly.',
    members: ['white-labs-wlp644', 'omega-oyl-200'],
  },
  {
    id: 'achouffe',
    name: 'Achouffe',
    description:
      'The Ardennes strain: phenolic and spicy, content to run warm, and unusual among Belgian yeasts in flocculating hard and cropping easily. Well-rounded fruit against balanced phenolics, and a range broad enough that it does not specialize.',
    members: ['wyeast-3522', 'white-labs-wlp550', 'omega-oyl-024', 'imperial-b45'],
  },
  {
    id: 'blaugies',
    name: 'Blaugies',
    description:
      'The Wallonian saison strain out of a small Belgian spelt brewery: peppery phenols, earthy spice and tropical fruit behind, finishing bone dry. It runs hot like the classic saison yeasts but flocculates better than most of them.',
    members: ['wyeast-3726', 'omega-oyl-042', 'imperial-b56'],
  },
  {
    id: 'french-saison',
    name: 'French saison',
    description:
      'The saison strain that will take a beer drier than you planned: pepper and lemon zest over relentless attenuation. It carries STA1, so it goes on chewing in package — plan the packaging around it rather than hoping it stops.',
    members: ['wyeast-3711', 'omega-oyl-026'],
  },
  {
    id: 'charles-wells',
    name: 'Charles Wells',
    description:
      'The Bedford brewery strain: a deliberately low attenuator that leaves body and a rounded fruity sweetness behind, with a distinct ester profile over the top. Useful whenever a mild, a bitter or a brown should finish fuller than the numbers suggest.',
    members: ['white-labs-wlp006', 'fermentis-s-33'],
  },
];
